<script lang="ts" setup>
const emit = defineEmits(["continue", "back"]);
defineProps({
  showWarning: {
    type: Boolean,
    default: true,
  },
});
const { createRecoveryKitPdf } = usePDF();
const userStore = useUserStore();

type State = "start" | "input" | "success";
const state = ref<State>("start");
const checked = ref(false);
const isDownloaded = ref(false);
const isLoading = ref(false);
const phrase = ref("");
const inputPhrase = ref("");
const error = ref(false);

const handleContinue = () => {
  state.value = "input";
};

const handleInputPhrase = () => {
  if (inputPhrase.value === phrase.value) {
    state.value = "success";
    isLoading.value = true;
    emit("continue", {
      method: "recoveryPassword",
      value: phrase.value,
    });
  } else {
    error.value = true;
  }
};

const handleBack = () => {
  state.value = "start";
  error.value = false;
  checked.value = false;
  inputPhrase.value = "";
};

onMounted(async () => {
  phrase.value = generateRandomString(64);
});

const onDownload = async () => {
  await createRecoveryKitPdf(userStore.email, phrase.value);
  isDownloaded.value = true;
};
</script>

<template>
  <div>
    <div class="flex gap-2 font-bold flex-wrap">
      <h3 class="text-neutral-200">
        {{ $t("main.modalUserWalletCreate.GFALRecoveryKit") }}
      </h3>
      <h3 class="text-error-300">
        {{ $t("main.modalUserWalletCreate.recoveryKitCaution") }}
      </h3>
    </div>
    <div
      v-if="state === 'start'"
      class="text-neutral-300 flex flex-col gap-4 mt-4">
      <p>
        {{ $t("main.modalUserWalletCreate.recoveryKitInfo") }}
      </p>

      <div
        class="bg-neutral-500 rounded-xl p-4 flex items-center justify-between gap-4 cursor-pointer"
        :class="
          isDownloaded
            ? 'border border-primary-500'
            : 'group hover:text-primary-500'
        "
        @click="onDownload">
        <NuxtIcon
          name="mdi:shield-key"
          class="w-7 h-7 text-neutral-200 group-hover:text-primary-500" />
        <div class="flex-1 flex flex-col">
          <p class="font-bold text-neutral-200 group-hover:text-primary-500">
            {{ $t("main.modalUserWalletCreate.GFALRecoveryKit") }}
            <span class="text-error-300 font-normal">{{
              $t("main.modalUserWalletCreate.recoveryKitRequired")
            }}</span>
          </p>
          <p class="text-sm group-hover:text-primary-500">
            {{ $t("main.modalUserWalletCreate.downloadRecoveryKit") }}
          </p>
        </div>
        <NuxtIcon
          v-if="!isDownloaded"
          name="mdi:tray-download"
          class="w-6 h-6 text-neutral-200 group-hover:text-primary-500" />
        <NuxtIcon v-else name="mdi:check" class="w-6 h-6 text-primary-500" />
      </div>
      <label class="flex items-center gap-2">
        <div class="relative flex items-center justify-center">
          <input
            type="checkbox"
            v-model="checked"
            class="w-4 h-4 rounded-xs bg-white border border-primary-500 accent-primary-500 z-0" />
        </div>
        <p class="text-sm">
          {{ $t("main.modalUserWalletCreate.recoveryKitAccepted") }}
        </p>
      </label>
      <div class="w-full flex gap-4">
        <BaseButtonCancel
          :text="$t('main.modalUserWalletCreate.cancel')"
          size="lg"
          class="w-full"
          @click="emit('back')" />
        <BaseButtonSubmit
          :text="$t('main.modalUserWalletCreate.continue')"
          size="lg"
          class="w-full"
          :disabled="!checked || !isDownloaded"
          @click="handleContinue" />
      </div>
    </div>
    <div
      v-else-if="state === 'input'"
      class="text-neutral-300 flex flex-col gap-4">
      <p class="mt-4">
        {{ $t("main.modalUserWalletCreate.recoveryKitInput") }}
      </p>
      <form @submit.prevent="handleInputPhrase">
        <BaseInputPassword
          v-model="inputPhrase"
          :label="$t('main.modalUserWalletCreate.yourRecoveryKey')"
          :placeholder="
            $t('main.modalUserWalletCreate.recoveryPasswordPlaceholder')
          "
          :id="'recovery-password'" />
        <div
          class="bg-neutral-900 rounded-xl p-4 text-warning-300 flex items-start gap-2 my-4">
          <NuxtIcon name="mdi:warning" class="w-4 h-4 shrink-0 mt-1" />

          <p class="text-xs">
            {{ $t("main.modalUserWalletCreate.recoveryKitImportant") }}
          </p>
        </div>
        <p v-if="error" class="text-red-500">
          {{ $t("main.modalUserWalletCreate.recoveryPasswordIncorrect") }}
        </p>
        <div class="w-full flex gap-4">
          <BaseButtonCancel
            :text="$t('main.modalUserWalletCreate.cancel')"
            size="lg"
            class="w-full"
            @click="handleBack" />
          <BaseButtonSubmit
            :text="$t('main.modalUserWalletCreate.continue')"
            size="lg"
            class="w-full"
            :disabled="!checked || !isDownloaded"
            @click="handleContinue" />
        </div>
      </form>
    </div>
    <div v-else>
      <ModalLoader
        :title="$t('modalUserWalletCreate.creatingWallet')"
        :body-subtitle="''" />
    </div>
  </div>
</template>
